import {
    useCallback,
    useEffect,
    useEffectEvent,
    useRef,
    useState,
    useSyncExternalStore,
} from "react";
import * as Sentry from "@sentry/react";
import { Task } from "@common/types/task";
import { FiltersTypes } from "@common/types/filters/general";
import { useStickyRefreshTask } from "@api/task.api";

interface UseStickyRefreshOnMountParams {
    selectedTask: Task;
    /**
     * Invalidate the list query backing this task body (transactions,
     * counterparties or documents) so newly-added members become visible.
     */
    onMembershipAdded: () => void;
}

export interface UseStickyRefreshOnMountResult {
    /**
     * True while a sticky task with an empty membership snapshot is still
     * waiting on the first refresh.
     */
    isMembershipPending: boolean;
    /**
     * Membership IDs returned by the refresh itself. Apply these to the
     * open table instead of waiting for the selected task cache to update.
     */
    resolvedMembership: FiltersTypes | undefined;
}

type StickyMembershipListener = () => void;

// Listeners are scoped per task so resolving one task's membership only
// re-renders the bodies subscribed to that task, not every mounted sticky body.
const stickyMembershipListenersByTaskId = new Map<
    string,
    Set<StickyMembershipListener>
>();
const stickyMembershipByTaskId = new Map<string, FiltersTypes>();

function subscribeStickyMembership(
    taskId: string,
    listener: StickyMembershipListener,
) {
    let listeners = stickyMembershipListenersByTaskId.get(taskId);
    if (!listeners) {
        listeners = new Set();
        stickyMembershipListenersByTaskId.set(taskId, listeners);
    }
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
        // Once the last body for a task unmounts, drop its entries so the maps
        // stay bounded by the tasks currently open rather than every task
        // visited in the session. A remount re-runs the refresh from scratch.
        if (listeners.size === 0) {
            stickyMembershipListenersByTaskId.delete(taskId);
            stickyMembershipByTaskId.delete(taskId);
        }
    };
}

function getStickyMembershipSnapshot(taskId: string): FiltersTypes | undefined {
    return stickyMembershipByTaskId.get(taskId);
}

function getStickyMembershipServerSnapshot(): FiltersTypes | undefined {
    return undefined;
}

function setStickyMembership(taskId: string, membership: FiltersTypes) {
    stickyMembershipByTaskId.set(taskId, membership);
    stickyMembershipListenersByTaskId
        .get(taskId)
        ?.forEach((listener) => listener());
}

export function hasStickyMembershipSnapshot(
    filters: FiltersTypes | null | undefined,
): boolean {
    return (
        (filters?.transactionIds?.length ?? 0) > 0 ||
        (filters?.counterpartiesIds?.length ?? 0) > 0 ||
        (filters?.billIds?.length ?? 0) > 0 ||
        (filters?.invoiceIds?.length ?? 0) > 0 ||
        (filters?.financialDocumentIds?.length ?? 0) > 0
    );
}

/**
 * Entities a sticky task's query is scoped to. The create snapshot keeps
 * them in `originalFilters` only; `entityIds` is the task's own entities.
 */
export function stickyScopeEntityIds(task: Task): number[] {
    const originalEntityIds = task.view?.originalFilters?.entityIds ?? [];
    return originalEntityIds.length > 0
        ? originalEntityIds
        : (task.entityIds ?? []);
}

interface MergeStickyMembershipFiltersParams {
    viewFilters: FiltersTypes | undefined;
    resolvedMembership: FiltersTypes | undefined;
    scopeEntityIds: number[];
}

/**
 * The transactions query sends nothing while `entityIds` is empty, so a
 * membership to show is scoped to the task entities. An empty membership
 * stays unscoped: with no ids to pin, an entity scope alone would list
 * every transaction of the entity.
 */
export function mergeStickyMembershipFilters({
    viewFilters,
    resolvedMembership,
    scopeEntityIds,
}: MergeStickyMembershipFiltersParams): FiltersTypes {
    const merged: FiltersTypes = {
        ...viewFilters,
        ...resolvedMembership,
    };
    const hasEntityScope = (merged.entityIds?.length ?? 0) > 0;
    if (hasEntityScope || !hasStickyMembershipSnapshot(merged)) {
        return merged;
    }
    return { ...merged, entityIds: scopeEntityIds };
}

/**
 * Filters the table is allowed to query with. `undefined` means do not
 * mount or query — the empty create snapshot must not be used as a query.
 */
export function getMountedStickyTableFilters({
    isMembershipPending,
    viewFilters,
    resolvedMembership,
    scopeEntityIds,
}: {
    isMembershipPending: boolean;
} & MergeStickyMembershipFiltersParams): FiltersTypes | undefined {
    if (isMembershipPending) {
        return undefined;
    }
    return mergeStickyMembershipFilters({
        viewFilters,
        resolvedMembership,
        scopeEntityIds,
    });
}

/**
 * Outcome of the single on-mount refresh for a sticky task.
 * - `pending`: the request is still in flight.
 * - `resolved`: the request succeeded — membership (possibly empty) is known.
 * - `failed`: the request errored — membership is still unknown.
 */
export type StickyRefreshOutcome = "pending" | "resolved" | "failed";

/**
 * Explicit readiness of a task body's membership:
 * - `ready`: membership is known (plain task or a snapshot is present) — mount.
 * - `pending`: sticky membership is not yet known — gate the table on a spinner.
 * - `empty`: the refresh confirmed the task genuinely has no members — mount so
 *   the empty state (with "Mark Done") can render.
 */
export type StickyMembershipReadiness = "ready" | "pending" | "empty";

export function getStickyMembershipReadiness({
    isMembershipKnown,
    refreshOutcome,
}: {
    isMembershipKnown: boolean;
    refreshOutcome: StickyRefreshOutcome;
}): StickyMembershipReadiness {
    if (isMembershipKnown) {
        return "ready";
    }
    // Only a successful refresh may confirm emptiness. A failed refresh keeps
    // the table gated so a transient error never surfaces a false empty state
    // that offers "Mark Done" for a task whose membership never loaded.
    return refreshOutcome === "resolved" ? "empty" : "pending";
}

function membershipFromRefresh(result: {
    transactionIds?: number[];
    counterpartiesIds?: string[];
    billIds?: string[];
    invoiceIds?: string[];
    financialDocumentIds?: number[];
}): FiltersTypes | undefined {
    const membership: FiltersTypes = {
        ...(result.transactionIds?.length
            ? { transactionIds: result.transactionIds }
            : {}),
        ...(result.counterpartiesIds?.length
            ? { counterpartiesIds: result.counterpartiesIds }
            : {}),
        ...(result.billIds?.length ? { billIds: result.billIds } : {}),
        ...(result.invoiceIds?.length ? { invoiceIds: result.invoiceIds } : {}),
        ...(result.financialDocumentIds?.length
            ? { financialDocumentIds: result.financialDocumentIds }
            : {}),
    };
    return hasStickyMembershipSnapshot(membership) ? membership : undefined;
}

/**
 * Refreshes a sticky task's membership snapshot once on mount. Plain
 * (non-sticky) tasks have no `originalFilters`, so the network call is skipped.
 * Membership IDs from the refresh response are returned so the open body can
 * query them immediately.
 */
export function useStickyRefreshOnMount({
    selectedTask,
    onMembershipAdded,
}: UseStickyRefreshOnMountParams): UseStickyRefreshOnMountResult {
    const isSticky = !!selectedTask.view?.originalFilters;
    const taskId = selectedTask.id;
    const subscribe = useCallback(
        (listener: StickyMembershipListener) =>
            subscribeStickyMembership(taskId, listener),
        [taskId],
    );
    const resolvedMembership = useSyncExternalStore(
        subscribe,
        () => getStickyMembershipSnapshot(taskId),
        getStickyMembershipServerSnapshot,
    );
    const isMembershipKnown =
        !isSticky ||
        hasStickyMembershipSnapshot(selectedTask.view?.filters) ||
        hasStickyMembershipSnapshot(resolvedMembership);
    const [refreshOutcome, setRefreshOutcome] = useState<StickyRefreshOutcome>(
        isMembershipKnown ? "resolved" : "pending",
    );
    const { mutateAsync: stickyRefresh } = useStickyRefreshTask(
        selectedTask.workspaceId,
        selectedTask.id,
    );
    const onMembershipAddedRef = useRef(onMembershipAdded);
    onMembershipAddedRef.current = onMembershipAdded;

    const startStickyRefresh = useEffectEvent(() => {
        if (!isSticky) {
            return;
        }
        let isCancelled = false;

        async function refreshStickyMembership() {
            try {
                const result = await stickyRefresh();
                const membership = membershipFromRefresh(result);
                if (membership) {
                    setStickyMembership(taskId, membership);
                }
                if (isCancelled) {
                    return;
                }
                if (membership) {
                    onMembershipAddedRef.current();
                }
                setRefreshOutcome("resolved");
            } catch (error) {
                Sentry.captureException(error, {
                    tags: { feature: "sticky-task-refresh" },
                    extra: {
                        taskId,
                        workspaceId: selectedTask.workspaceId,
                    },
                });
                if (!isCancelled) {
                    setRefreshOutcome("failed");
                }
            }
        }

        void refreshStickyMembership();
        return () => {
            isCancelled = true;
        };
    });
    // Run once on mount — component remounts on task switch via key={selectedTask.id}
    useEffect(() => startStickyRefresh(), []);

    return {
        isMembershipPending:
            getStickyMembershipReadiness({
                isMembershipKnown,
                refreshOutcome,
            }) === "pending",
        resolvedMembership,
    };
}
